import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { UserPicker } from '../features/users/UserPicker';
import type { DirectoryUserDto, SoftwareAssignmentDto } from '../api/types';
import './devices.css';

const INTENT_LABEL: Record<string, string> = {
  required: 'Erforderlich',
  available: 'Verfügbar',
  uninstall: 'Deinstallieren',
  availableWithoutEnrollment: 'Verfügbar (ohne Enrollment)',
};

type SortCol = 'app' | 'group' | 'intent' | 'status';

export function SoftwareUserTab() {
  const api = useApi();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'devices.software')?.ticketMode ?? 'None';

  const [selected, setSelected] = useState<DirectoryUserDto | undefined>();
  const [rows, setRows] = useState<SoftwareAssignmentDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [ticket, setTicket] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'app', dir: 'asc' });

  async function pick(u: DirectoryUserDto | undefined) {
    setSelected(u);
    setRows(null);
    setError(null);
    setNotice(null);
    if (!u) return;
    setLoading(true);
    try {
      setRows(await api.getUserSoftware(u.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function act(r: SoftwareAssignmentDto) {
    if (!selected) return;
    if (ticketMode === 'Required' && !ticket.trim()) {
      setError('Ticketnummer ist erforderlich.');
      return;
    }
    const key = r.appId + r.groupId;
    setBusyKey(key);
    setError(null);
    setNotice(null);
    try {
      const res = await api.setSoftwareMembership({
        userId: selected.id,
        userDisplay: selected.displayName,
        groupId: r.groupId,
        groupName: r.groupName,
        appName: r.appName,
        assign: !r.assigned,
        ticketNumber: ticket.trim() || undefined,
      });
      if (res.status === 'PendingApproval') {
        setNotice('Antrag eingereicht — die Änderung erscheint nach Freigabe unter „Meine Anfragen".');
      } else {
        setNotice(res.message ?? 'Erledigt.');
        setRows(await api.getUserSoftware(selected.id));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (rows ?? []).filter(
      (r) =>
        q === '' ||
        [r.appName, r.groupName, r.intent, r.appType].some((f) => (f ?? '').toLowerCase().includes(q)),
    );
    const cmp = (a: SoftwareAssignmentDto, b: SoftwareAssignmentDto) => {
      let v = 0;
      if (sort.col === 'app') v = a.appName.localeCompare(b.appName);
      else if (sort.col === 'group') v = a.groupName.localeCompare(b.groupName);
      else if (sort.col === 'intent') v = (a.intent ?? '').localeCompare(b.intent ?? '');
      else if (sort.col === 'status') v = Number(a.assigned) - Number(b.assigned);
      return sort.dir === 'asc' ? v : -v;
    };
    return [...list].sort(cmp);
  }, [rows, search, sort]);

  function head(label: string, col: SortCol) {
    const active = sort.col === col;
    return (
      <th
        style={{ cursor: 'pointer' }}
        onClick={() => setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }))}
      >
        {label}
        {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </th>
    );
  }

  return (
    <div style={{ maxWidth: 1000, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
      <Field label="Benutzer">
        <UserPicker onSelect={pick} />
      </Field>

      {ticketMode !== 'None' ? (
        <Field label="Ticketnummer" required={ticketMode === 'Required'} hint={ticketMode === 'Required' ? 'Pflicht für Zuweisungen.' : 'Optional.'}>
          <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. REQ0012345" style={{ maxWidth: 280 }} />
        </Field>
      ) : null}

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {notice ? (
        <MessageBar intent="success">
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      ) : null}
      {loading ? <Spinner size="tiny" label="Lade Software-Zuweisungen…" /> : null}

      {selected && rows && rows.length === 0 ? (
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          Keine gruppenbasierten App-Zuweisungen gefunden.
        </Caption1>
      ) : null}

      {rows && rows.length > 0 ? (
        <>
          <Input
            style={{ maxWidth: 280 }}
            placeholder="App/Gruppe/Intent suchen…"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<SearchRegular />}
          />
          <div className="dev-tablewrap">
            <table className="dev-table">
              <thead>
                <tr>
                  {head('App', 'app')}
                  <th>Typ</th>
                  {head('Zuweisungs-Gruppe', 'group')}
                  {head('Intent', 'intent')}
                  {head('Status', 'status')}
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const key = r.appId + r.groupId;
                  return (
                    <tr key={key}>
                      <td>
                        <Text weight="semibold" size={200}>
                          {r.appName}
                        </Text>
                      </td>
                      <td>{r.appType ?? '—'}</td>
                      <td className="pii">{r.groupName}</td>
                      <td>{r.intent ? (INTENT_LABEL[r.intent] ?? r.intent) : '—'}</td>
                      <td>
                        <Badge appearance="tint" color={r.assigned ? 'success' : 'subtle'}>
                          {r.assigned ? 'zugewiesen' : 'nicht zugewiesen'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="small"
                          appearance={r.assigned ? 'subtle' : 'primary'}
                          disabled={busyKey !== null}
                          onClick={() => act(r)}
                        >
                          {busyKey === key ? '…' : r.assigned ? 'Entfernen' : 'Zuweisen'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
